import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    // Set video sources
    for (const source of videoSources) {
      const { videoSource } = source.dataset;
      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();

    // Wait until video is ready before attempting to play
    videoElement.addEventListener('canplay', () => {
      this.attemptPlay(videoElement);
    }, { once: true });

    // IntersectionObserver — pause when off screen, play when back in view
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.attemptPlay(videoElement);
          } else {
            videoElement.pause();
          }
        }
      },
      { threshold: 0.1 }
    );

    this.observer.observe(this);

    // Retry on user interaction — covers Low Power Mode and Safari blocks
    this.boundRetry = () => this.attemptPlay(videoElement);
    document.addEventListener('touchstart', this.boundRetry, { once: true });
    document.addEventListener('click', this.boundRetry, { once: true });
  }

  attemptPlay(videoElement) {
    if (videoElement.paused) {
      videoElement.play().catch(() => {});
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up observer and event listeners
    if (this.observer) this.observer.disconnect();
    document.removeEventListener('touchstart', this.boundRetry);
    document.removeEventListener('click', this.boundRetry);
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}